import { MenuDivider } from "@blueprintjs/core"
import TablePagination from "components/Molecules/TablePagination"
import ActionMenu from "components/Organisms/ActionMenu"
import ActionMenuItem from "components/Organisms/ActionMenuItem"
import NewResourceButton from "components/Organisms/AddResourceButton"
import ModuleHeader, { Description } from "components/Templates/ModuleHeader"
import { p_canImportStock, p_canFetchBatchStock } from "constants/permissions"
import {
  usePeriodicTasksLazyQuery,
  useFetchBatchStockStatusQuery,
  useMutFetchBatchStockMutation,
} from "generated/graphql"
import { useSearchFilter } from "hooks/searchFilterHooks"
import useAuth from "hooks/useAuth"
import { useAuthorizationSimple } from "hooks/useAuthorizationStrict"
import ReTriggerDialog from "pages/data/ReTriggerDialog"
import React, { useEffect } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { useCurrentDcId } from "store/auth.redux"
import StockManagementFilter from "./StockManagementFilter"
import CustomButtonGroup from "components/Atoms/CustomButtonGroup"
import CustomButton from "components/Atoms/CustomButton"

const StockManagementHeader = () => {
  const [{ search, page }, setFilter] = useSearchFilter()
  const isAuthorized = useAuthorizationSimple(p_canImportStock)
  const canFetchBatchStock = useAuthorizationSimple(p_canFetchBatchStock)

  const me = useAuth()
  const department = me?.user?.currentDepartment
  const dcId = useCurrentDcId()
  const [fetchTasks, { loading, data }] = usePeriodicTasksLazyQuery()
  const syncTaskId = data?.allPeriodicTasks?.[0]?.id
  const syncTaskName = data?.allPeriodicTasks?.[0]?.name
  useEffect(() => {
    if (dcId && department?.id && department?.hasDynamicStock) {
      fetchTasks({
        variables: {
          first: 1,
          query: `SyncStock.${dcId}.${department.id}`,
          filter: {
            task: "sap.fetch_item_stock",
          },
        },
      })
    }
  }, [department, dcId])

  const {
    data: batchStatusData,
    loading: batchStatusLoading,
    refetch,
  } = useFetchBatchStockStatusQuery({ fetchPolicy: "network-only" })
  const canExecuteBatchSync =
    batchStatusData?.fetchBatchStockStatus?.canExecute === true

  const [fetchBatchStock, { loading: fetchBatchLoading }] =
    useMutFetchBatchStockMutation()

  const navigate = useNavigate()
  const location = useLocation()

  const activeTab = location.pathname === "/d/ecom/stock" ? "stocks" : "batches"

  function handleClick(type: any) {
    switch (type) {
      case "document-share":
        navigate(`${location.pathname}/import/all`)
        return
      case "stock-transaction":
        navigate(`${location.pathname}/import/transactions`)
        return
      case "sync-stock":
        setFilter(prev => ({
          ...prev,
          task: syncTaskId,
          view: "trigger",
          name: syncTaskName,
        }))
        return
      case "fetch-batch-stock":
        fetchBatchStock({
          variables: { interval: 15 },
          refetchQueries: ["SellableItemsStockList"],
        }).then(() => refetch())
        return
      default:
        return
    }
  }
  return (
    <>
      <ModuleHeader
        filter={<StockManagementFilter />}
        borderBottom="none"
        query={search ?? ""}
        onQueryChange={search => {
          setFilter((prev: any) => ({
            ...prev,
            search,
            page: 0,
          }))
        }}
        white
        className="p-3"
        header={
          <div className="flex items-center">
            <Description icon="add-row-bottom" title="Stocks" />
            <CustomButtonGroup joint className="ml-3">
              <CustomButton
                intent={activeTab === "stocks" ? "primary" : null}
                icon={activeTab === "stocks" ? "th" : null}
                text="Stocks"
                onClick={() => navigate("/d/ecom/stock")}
              />
              <CustomButton
                intent={activeTab === "batches" ? "primary" : null}
                icon={activeTab === "batches" ? "th" : null}
                text="Batches"
                onClick={() => navigate("/d/ecom/stock/batches")}
              />
            </CustomButtonGroup>
          </div>
        }
      >
        <TablePagination
          className="mr-3"
          page={page}
          onPageChange={page => {
            setFilter((prev: any) => ({
              ...prev,
              page,
            }))
          }}
        />
        <ActionMenu
          className="mr-3"
          onClick={handleClick}
          position="bottom-right"
        >
          <MenuDivider title="Import" />
          <ActionMenuItem
            disabled={!isAuthorized}
            text="Stock"
            label=".xlsx"
            icon="document-share"
          />
          <ActionMenuItem
            id="stock-transaction"
            disabled={!isAuthorized}
            text="Transactions"
            label=".xlsx"
            icon="document-share"
          />
          {department?.hasDynamicStock && (
            <>
              <MenuDivider title="Sync" />
              <ActionMenuItem
                disabled={!isAuthorized || loading || !syncTaskId}
                text="Stock"
                icon="refresh"
                id="sync-stock"
              />
            </>
          )}

          {canFetchBatchStock && (
            <>
              <MenuDivider title="Batch Sync" />
              <ActionMenuItem
                disabled={
                  !canFetchBatchStock ||
                  batchStatusLoading ||
                  fetchBatchLoading ||
                  !canExecuteBatchSync
                }
                text="Fetch Batch Stock"
                icon="cloud-download"
                id="fetch-batch-stock"
              />
            </>
          )}
        </ActionMenu>

        <NewResourceButton module="stock" to="/d/ecom/stock/sellable-items" />
      </ModuleHeader>
      <ReTriggerDialog />
    </>
  )
}

export default StockManagementHeader
